import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import ColorSelector from 'components/ColorSelector'
import ImageSelector from 'components/CreateStreamForm/ImageSelector'
import FileFolderCard from 'components/FileFolderCard'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import { FileFolder } from 'interfaces'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { message } from 'services/snackbar'
import { getRandomElements } from 'shared/utils'
import { ColorGradient, defaultStreamColor, getGradientFromString } from 'utils/colors'
import styles from './CreateFileFolderForm.pcss'

const MAX_FOLDER_NAME_LENGTH = 50

export interface FooterState {
  canSubmit: boolean
  label: React.ReactNode
  submit: () => void
}

interface CreateFileFolderFormProps {
  loading: boolean
  filesCount: number
  images: string[]
  updateFolder?: FileFolder
  onSubmit: (
    name: string,
    color: ColorGradient,
    isPrivate?: boolean,
    featuredImage?: string | File,
    coverImages?: string[]
  ) => boolean | void
  onCancel?: () => void
  isPrivateOptionAllowed?: () => boolean
  externalChrome?: boolean
  onFooterStateChange?: (state: FooterState) => void
}

export function CreateFileFolderForm(props: CreateFileFolderFormProps) {
  const { images } = props
  const intl = useIntl()
  const [imageSelectorImages, setImageSelectorImages] = React.useState<Record<string, { original?: string }>>({})
  const dispatch = useDispatch()
  const [name, setName] = React.useState(props.updateFolder?.name || '')
  const [color, setColor] = React.useState(props.updateFolder ? getGradientFromString(props.updateFolder.color) : defaultStreamColor)
  const [imageFile, setImageFile] = React.useState<File | null>(null)
  const [imageSrc, setImageSrc] = React.useState(props.updateFolder?.featuredImage || images[0])
  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(props.updateFolder ? null : 0)
  const [secondaryImages, setSecondaryImages] = React.useState<string[]>(props.updateFolder?.coverImages || [])
  const [isPrivate, setIsPrivate] = React.useState(props.updateFolder?.isPrivate || false)
  const [colorPickerKey, setColorPickerKey] = React.useState(0)
  // Display single image when folder files are less than 3 or when creating new folder
  const singleImageMode = !props.updateFolder || images.length <= 2

  React.useEffect(() => {
    setImageSelectorImages(current => {
      return images.reduce((acc, image) => {
        acc[image] = { original: current[image]?.original }
        return acc
      }, {} as Record<string, { original?: string }>)
    })
  }, [images])

  React.useEffect(() => {
    if ((!imageSrc || images.indexOf(imageSrc) === -1) && !props.updateFolder && images[0]) {
      setImageSrc(images[0])
      setSelectedImageIndex(0)
    }
  }, [imageSrc, images, props.updateFolder])

  const resetForm = () => {
    setName('')
    setColor(defaultStreamColor)
    setImageFile(null)
    setImageSrc('')
    setIsPrivate(false)
    setSecondaryImages([])
    setSelectedImageIndex(null)
    setColorPickerKey(current => current + 1)
  }

  const toggleIsPrivate = () => {
    setIsPrivate(current => !current)
  }

  const onCancel = () => {
    if (props.onCancel) {
      props.onCancel()
    }
    resetForm()
  }

  const onSubmit = () => {
    const folderName = name.trim()
    if (folderName.length === 0) {
      dispatch(message('Please enter folder name', 'warning'))
      return
    }

    if (folderName.length < 2) {
      dispatch(message('Folder name should be at least 2 characters long', 'warning'))
      return
    }

    if (folderName.length > MAX_FOLDER_NAME_LENGTH) {
      dispatch(message(`Folder name should be less than ${MAX_FOLDER_NAME_LENGTH} characters long`, 'warning'))
      return
    }

    if (isPrivate && props.isPrivateOptionAllowed && !props.isPrivateOptionAllowed()) {
      return
    }

    let featuredImage: any = imageFile
    if (!featuredImage && typeof selectedImageIndex === 'number') {
      featuredImage = images[selectedImageIndex]
    }

    const skipReset = props.onSubmit(name, color, isPrivate, featuredImage, secondaryImages)
    if (!skipReset) {
      resetForm()
    }
  }

  const submitRef = React.useRef<() => void>(() => {})
  submitRef.current = onSubmit
  const stableSubmit = React.useCallback(() => submitRef.current(), [])

  React.useEffect(() => {
    if (!props.onFooterStateChange) {
      return
    }
    props.onFooterStateChange({
      canSubmit: name.trim().length > 0 && !props.loading,
      label: (
        <FormattedMessage
          id={props.updateFolder ? 'uploads.popup.actions.update-folder' : 'uploads.popup.actions.create-folder'}
          values={{ count: props.filesCount }}
        />
      ),
      submit: stableSubmit
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, props.loading, props.filesCount, props.updateFolder, stableSubmit, props.onFooterStateChange])

  const randomizeSecondaryImages = (excludeIndex?: number) => {
    if (singleImageMode) {
      return
    }
    const secondarySelection = [...images]
    if (typeof excludeIndex === 'number') {
      secondarySelection.splice(excludeIndex, 1)
    }
    const randomPick = getRandomElements(secondarySelection, 2)
    setSecondaryImages(randomPick)
  }

  const onFileChanged = (file: File) => {
    setImageFile(file)
    const fileReader = new FileReader()
    fileReader.onload = () => {
      setImageSrc(fileReader.result as string)
      setSelectedImageIndex(null)
      randomizeSecondaryImages()
    }
    fileReader.onerror = () => console.error('File could not be loaded.')
    fileReader.readAsDataURL(file)
  }

  const onSelectedImageIndexChange = (index: number) => {
    setImageSrc(images[index] || '')
    setSelectedImageIndex(index)
    randomizeSecondaryImages(index)
    setImageFile(null)
  }

  const onValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setName(value)
  }

  const accessSelectOptions: PPSelectOptions = {
    private: { label: 'Private (me only)' },
    public: { label: 'Public (available to team members)' }
  }

  const onImageLoadFailed = (src: string) => {
    setImageSelectorImages(current => {
      return {
        ...current,
        [src]: { original: src.replace('/300xauto/', '/o/') }
      }
    })
  }

  return (
    <div className={`${styles.wrapper} ${props.externalChrome ? styles.external : ''}`}>
      <div className={styles.row}>
        <div className={styles['preview-col']}>
          <TextField
            value={name}
            disabled={props.loading}
            name="folder-name"
            placeholder={intl.formatMessage({ id: 'uploads.hint.input-name' })}
            classes={{ root: styles['input-box'] }}
            onChange={onValueChanged}
          />
          <FileFolderCard
            folder={{
              name: name || intl.formatMessage({ id: 'uploads.labels.new-folder-empty-name' }),
              color: `${color.from},${color.to}`,
              files: {},
              filesCount: props.filesCount,
              filters: [],
              order: 0,
              id: 'new',
              isFileFolder: true,
              isPrivate: false,
              featuredImage: imageSrc,
              coverImages: secondaryImages
            }}
            noHover
            hideFileCount
            disableNavigation
            className={styles['file-folder-preview']}
          />
        </div>
        <div className={styles['row-value']}>
          <div className={styles['color-box']}>
            <ColorSelector
              key={colorPickerKey}
              initialColor={color}
              className={styles['color-selector']}
              onSelectionChanged={setColor as any}
            />
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles['row-label']}>
          Cover image
        </div>
        <div className={styles['row-value']}>
          <ImageSelector
            imageUrls={Object.keys(imageSelectorImages).map(key => imageSelectorImages[key].original || key)}
            imageFileSrc={imageFile ? imageSrc : undefined}
            selectedIndex={selectedImageIndex}
            className={styles['img-selector']}
            onFileChanged={onFileChanged}
            onSelectedIndexChange={onSelectedImageIndexChange}
            onImageLoadFailed={onImageLoadFailed}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles['row-label']}>
          Accessibility
        </div>
        <div className={styles['row-value']}>
          <PPSelect
            raised
            options={accessSelectOptions}
            selectedValue={isPrivate ? 'private' : 'public'}
            onSelectionChange={toggleIsPrivate}
          />
        </div>
      </div>
      {!props.externalChrome && (
        <div className={styles.actions}>
          <Button variant="text" onClick={onCancel}>
            <FormattedMessage id="actions.cancel" defaultMessage="cancel" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={name.trim().length === 0 || props.loading}
            className={styles['btn-save']}
            onClick={onSubmit}
          >
            <FormattedMessage
              id={props.updateFolder ? 'uploads.popup.actions.update-folder' : 'uploads.popup.actions.create-folder'}
              values={{ count: props.filesCount }}
            />
          </Button>
        </div>
      )}
    </div>
  )
}

export default CreateFileFolderForm
